import { Grid, IconButton, Paper, Typography } from "@mui/material";
import { useState } from "react";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from "react-i18next";


interface CardFooterButtonProps {
  Icon: React.ElementType;
  text: string;
  disabled?: boolean;
  onClick: () => void;
  sx?: any;
}

export const CardFooterButton = ({ Icon, text, disabled, onClick, sx }: CardFooterButtonProps) => (
  <IconButton disabled={disabled} onClick={onClick} color="primary" sx={{ ...sx }}>
    <Icon sx={{ mx: "auto" }} />
    <Typography fontWeight={500} fontSize="10px">
      {text}
    </Typography>
  </IconButton>
);


export const CardFooterButtons = ({arrCrdFtrBtnProps = [], label}: {arrCrdFtrBtnProps: CardFooterButtonProps[], label: string}) => { 
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (arrCrdFtrBtnProps.length <= 4) {
    const mappedCardFooterToRender = arrCrdFtrBtnProps.map( ({ Icon, text, disabled, onClick, sx }, i) =>
      <CardFooterButton key={i}
        disabled={disabled}
        Icon={Icon}
        text={text}
        onClick={onClick}
        sx={sx}
      />
    );
    return (
      <>
        {mappedCardFooterToRender}
      </>
    );
  }

  const [buttonProps1, buttonProps2, buttonProps3, ...arrCrdFtrBtnMenuProps] = arrCrdFtrBtnProps;
  const arrCrdFtrBtnPropsRenderButton = [buttonProps1, buttonProps2, buttonProps3]
                  .map( ({ Icon, text, disabled, onClick, sx }, i) =>
                      <CardFooterButton key={i} disabled={disabled} Icon={Icon} text={text} onClick={onClick} sx={sx} />
                  );

  return (
    <>
      {arrCrdFtrBtnPropsRenderButton}
      <Tooltip title={label}>
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVertIcon color="primary"/>
            <Typography fontWeight={500} fontSize="10px" color="primary">
              {t("more")}
            </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        id="long-menu"
        PaperProps={{elevation: 1}}
        MenuListProps={{
          'aria-labelledby': 'long-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {arrCrdFtrBtnMenuProps.map(({ Icon, text, disabled, onClick, sx }, i) => (
          <MenuItem key={i+text} onClick={() => { onClick(); handleClose();}} disabled={disabled} color="primary">
            <Icon sx={{ mr: "5px" }} color="primary" />
            <Typography fontWeight={500} fontSize="10px" color="primary">
              {text}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface Props {
  body: React.ReactNode;
  actions: React.ReactNode;
}

const getCardFooterSX = (actions?: React.ReactElement) => {
  const actionProps = actions?.props as { arrCrdFtrBtnProps?: CardFooterButtonProps[] } | undefined;
  if (!actionProps || !actionProps.arrCrdFtrBtnProps) return {};
  let childrenCount = actionProps.arrCrdFtrBtnProps.length;
  return {
    justifyContent: childrenCount >= 4 ? "space-between" : "flex-start",
    gap: childrenCount >= 4 ? null : "10px",
  };
};

const CardWithFooter = ({ body, actions }: Props) => (
  <Paper sx={{ boxSizing: "border-box", height: "200px;" }}>
    <Grid container sx={{ height: "100%" }}>
      <Grid item sx={{ height: "75%", width: "100%", pt: 3, pb: 1, px: 3 }}>
        {body}
      </Grid>
      <Grid
        item
        sx={{ height: "25%", width: "100%", background: "#F9F9F9", px: 2, "& .MuiButtonBase-root": { padding: 0 } }}
      >
        <Grid container sx={{ width: "100%", height: "100%", ...getCardFooterSX(actions as React.ReactElement)  }} alignItems="center">
          {actions}
        </Grid>
      </Grid>
    </Grid>
  </Paper>
);

export default CardWithFooter;
